import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DesktopCollectionCardSkeleton,
  MobileCollectionCardSkeleton,
} from "../cards/DesktopCollectionCard";
import { inlineSign } from "../ui/DesktopChipRow";
import { SectionLink } from "../ui/SectionLink";
import { SectionTitle } from "../ui/SectionTitle";
import { ScrollArrowButton } from "../ui/ScrollArrowButton";

/**
 * One card of the band in each size, and the step the arrows scroll by: the card
 * and its gap-3. On mobile the row bleeds to the screen edge. A card takes 75% of
 * the row up to that edge, the right padding included, and the next card shows in
 * the other 25%: that peek says the row scrolls, so it has no arrows and no fog.
 */
const BAND_SIZES = {
  desktop: {
    section: "pt-5 pb-6 gap-4",
    header: "",
    row: "-m-1.5 scroll-px-1.5 p-1.5 pb-4",
    card: "w-74",
    cardStepPx: 308,
    Skeleton: DesktopCollectionCardSkeleton,
  },
  mobile: {
    section: "gap-3",
    header: "px-5",
    row: "scroll-px-5 px-5",
    card: "w-[calc((100%+1.25rem)*0.75)] max-w-74",
    cardStepPx: null,
    Skeleton: MobileCollectionCardSkeleton,
  },
} as const;

/** How wide the fog grows at an edge that hides more cards, in pixels. */
const FOG_MAX_PX = 32;

/** How much of the row each edge hides, in pixels, and the reading direction. */
type HiddenEdges = { start: number; end: number; sign: 1 | -1 };

/**
 * The row's fog, as a mask: an edge fades out only while cards hide past it,
 * so the first card sits sharp against the column before the reader scrolls
 * and the last one once the row reaches its end. The fog grows with the distance
 * the edge hides, so it comes in as the row moves and does not pop in at the
 * first pixel. A mask rather than a gradient overlay, so it blends into whatever
 * the band sits on.
 */
function fogMask(edges: HiddenEdges): string {
  const start = Math.min(FOG_MAX_PX, edges.start);
  const end = Math.min(FOG_MAX_PX, edges.end);
  return `linear-gradient(to ${edges.sign === 1 ? "right" : "left"}, transparent 0, black ${start}px, black calc(100% - ${end}px), transparent 100%)`;
}

/**
 * A horizontal band of collections in a feed (Figma "CollectionsBand"):
 * the section title and "See all" over a row of collection cards that scrolls
 * with a trackpad or a finger, and two arrows on desktop. The caller draws each
 * card, as a `DesktopCollectionCard` or a `MobileCollectionCard`.
 */
function CollectionsBand<TPlaylist extends { id: string }>({
  size,
  title,
  seeAllTo,
  playlists,
  isLoading,
  renderCard,
}: {
  size: keyof typeof BAND_SIZES;
  title: string;
  /** `null` draws no "See all": the band shows every collection it has. */
  seeAllTo: string | null;
  playlists: ReadonlyArray<TPlaylist>;
  isLoading: boolean;
  renderCard: (playlist: TPlaylist) => React.ReactNode;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const sizes = BAND_SIZES[size];
  const arrowStepPx = sizes.cardStepPx;
  // The row the arrows scroll, and which of its edges hide cards: UI
  // machinery, not page state.
  const rowRef = useRef<HTMLDivElement>(null);
  const [hiddenEdges, setHiddenEdges] = useState<HiddenEdges>({
    start: 0,
    end: 0,
    sign: 1,
  });

  useEffect(() => {
    const row = rowRef.current;
    if (row === null) return;
    function update(): void {
      if (row === null) return;
      const maxScroll = row.scrollWidth - row.clientWidth;
      const sign = inlineSign(row);
      const scrolled = row.scrollLeft * sign;
      // A subpixel scroll position is not a hidden card.
      setHiddenEdges({
        start: scrolled > 1 ? scrolled : 0,
        end: maxScroll - scrolled > 1 ? maxScroll - scrolled : 0,
        sign,
      });
    }
    update();
    row.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(row);
    return () => {
      row.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [playlists.length, isLoading]);

  if (!isLoading && playlists.length === 0) return null;

  function scrollByCard(direction: -1 | 1, stepPx: number) {
    const row = rowRef.current;
    if (row === null)
      throw new Error("should not happen: the band row is unmounted");
    row.scrollBy({
      left: direction * stepPx * inlineSign(row),
      behavior: "smooth",
    });
  }

  return (
    <section className={`flex flex-col ${sizes.section}`}>
      <div
        className={`flex items-center justify-between gap-3 ${sizes.header}`}
      >
        <SectionTitle title={title} />
        <div className="flex items-center gap-2">
          {seeAllTo !== null && playlists.length >= 2 ? (
            <SectionLink to={seeAllTo}>{t("See all")}</SectionLink>
          ) : null}
          {arrowStepPx !== null &&
          (hiddenEdges.start > 0 || hiddenEdges.end > 0) ? (
            <>
              <ScrollArrowButton
                direction="previous"
                onClick={() => {
                  scrollByCard(-1, arrowStepPx);
                }}
              />
              <ScrollArrowButton
                direction="next"
                onClick={() => {
                  scrollByCard(1, arrowStepPx);
                }}
              />
            </>
          ) : null}
        </div>
      </div>
      {/* The padding, given back by the margin, leaves room for a card's focus
          ring, which stands 6px off the card: a scroller clips both axes. The
          bottom keeps more, for the desktop scrollbar under the cards: flush
          with their counts, it read as an underline. */}
      <div
        ref={rowRef}
        className={`scrollbar-hidden flex snap-x snap-mandatory gap-3 overflow-x-auto ${sizes.row}`}
        style={
          arrowStepPx === null
            ? {}
            : {
                maskImage: fogMask(hiddenEdges),
                WebkitMaskImage: fogMask(hiddenEdges),
              }
        }
      >
        {isLoading
          ? [0, 1, 2].map((index) => (
              <div key={index} className={`${sizes.card} shrink-0 snap-start`}>
                <sizes.Skeleton />
              </div>
            ))
          : playlists.map((playlist) => (
              <div
                key={playlist.id}
                className={`${sizes.card} shrink-0 snap-start`}
              >
                {renderCard(playlist)}
              </div>
            ))}
      </div>
    </section>
  );
}

export { CollectionsBand };
